import { chipClasses, styled, tablePaginationClasses } from '@mui/material';
import {
  DataGridProProps,
  GridCallbackDetails,
  GridPagination,
  GridPaginationModel,
  GridValidRowModel,
  getDataGridUtilityClass,
  gridClasses,
  DataGridPro as MuiDataGridPro,
} from '@mui/x-data-grid-pro';
import { clsx } from 'clsx';
import { forwardRef } from 'react';
import { combineSx } from '../../utils';
import { CHIP_SMALL_HEIGHT } from '../Chip/chip-overrides';
import { useContainerContext } from '../Container/ContainerContextProvider';
import { StyledGridFilterPanel } from './FilterPanel';
import { DataTableProps } from './model';
import {
  ENABLE_EXPANDABLE_CELLS_CLASS_NAME,
  EXPANDABLE_CELL_FLEX_WRAP_CLASS_NAME,
  EXPANDABLE_CELL_NESTED_TEXT_CONTENT_CLASS_NAME,
  useExpandableCells,
} from './useExpandableCells';

export const DataTable = forwardRef<HTMLDivElement, DataTableProps>(function DataTable(
  {
    checkboxSelection = false,
    className,
    columnHeaderHeight = 40,
    columns: initColumns,
    compact = false,
    disableRowSelectionOnClick = true,
    disableRowSelectionOnClickWithCheckbox = false,
    enableExpandableCells = false,
    getRowHeight,
    hasNextPage,
    loading,
    onPaginationModelChange,
    pageSizeOptions = [10, 25, 50, 100],
    paginationModel,
    rowCount,
    rowHeight = 40,
    rows,
    slots,
    sx,
    ...props
  },
  ref
) {
  const { isNestedContainer } = useContainerContext();

  const {
    columns,
    sx: expandableCellsSx,
    className: expandableCellsClassName,
  } = useExpandableCells({
    enableExpandableCells,
    columns: initColumns,
    rowHeight,
    compact,
    getRowHeight,
  });

  // If the row count is -1, the pagination label should show "of more than X" instead of a total count
  // This is useful for cases where the row count is very large, such as when fetching data from a server
  // NOTE: This is API is supported in MUI v7, so we can remove this once we upgrade.
  const useUnknownRowTotal = rowCount === -1;

  return (
    <StyledDataGrid
      isNestedContainer={isNestedContainer}
      className={clsx(className, expandableCellsClassName)}
      checkboxSelection={checkboxSelection}
      disableRowSelectionOnClick={
        checkboxSelection ? disableRowSelectionOnClickWithCheckbox : disableRowSelectionOnClick
      }
      columnHeaderHeight={columnHeaderHeight}
      ref={ref}
      rowHeight={rowHeight}
      getRowHeight={getRowHeight}
      pageSizeOptions={pageSizeOptions}
      // Pagination is controlled using a custom pagination component when the row count is -1
      paginationModel={useUnknownRowTotal ? undefined : paginationModel}
      density={compact ? 'compact' : 'standard'}
      sx={combineSx(expandableCellsSx, sx)}
      columns={columns}
      slots={{
        filterPanel: StyledGridFilterPanel,
        pagination: useUnknownRowTotal
          ? (props) => (
              <UnknownRowTotalPagination
                {...props}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                hasNextPage={hasNextPage}
                loading={loading}
                rows={rows}
              />
            )
          : undefined,
        ...slots,
      }}
      onPaginationModelChange={useUnknownRowTotal ? undefined : onPaginationModelChange}
      loading={loading}
      rowCount={rowCount}
      rows={rows}
      {...props}
    />
  );
});

const UnknownRowTotalPagination = ({
  paginationModel,
  onPaginationModelChange,
  hasNextPage,
  loading,
  rows,
  ...props
}: {
  hasNextPage: boolean;
  loading: boolean;
  onPaginationModelChange: (model: GridPaginationModel, details: GridCallbackDetails) => void;
  paginationModel: GridPaginationModel;
  rows: GridValidRowModel[];
}) => {
  const pageSize = paginationModel?.pageSize ?? 10;
  const from = (paginationModel?.page ?? 0) * pageSize;
  return (
    <GridPagination
      {...props}
      onPageChange={(_, page) => onPaginationModelChange?.({ pageSize, page }, { reason: 'pagination' })}
      onRowsPerPageChange={(e) => {
        onPaginationModelChange?.({ pageSize: Number(e.target.value), page: 0 }, { reason: 'pageSize change' });
      }}
      rowsPerPage={pageSize}
      page={paginationModel?.page ?? 0}
      count={hasNextPage ? -1 : from + rows.length}
      nextIconButtonProps={{
        // disable next button when loading or when we reach the end of the logs
        disabled: loading || !hasNextPage,
      }}
    />
  );
};

type StyledDataGridRootProps = DataGridProProps & { isNestedContainer?: boolean };

const StyledDataGrid = styled(MuiDataGridPro, {
  shouldForwardProp: (prop) => prop !== 'isNestedContainer',
})<StyledDataGridRootProps>(({ theme, isNestedContainer }) => {
  const rowSelectedClassName = getDataGridUtilityClass('selected');
  const newBorderRadius = isNestedContainer ? theme.shape.borderRadiusMd : theme.shape.borderRadiusLg;

  return {
    ...(theme.featureFlags.panwRebrandStubYear
      ? {
          borderRadius: newBorderRadius,
          [`& .${gridClasses.footerContainer}`]: {
            borderColor: theme.palette.border.default,

            [`& .${tablePaginationClasses.root}`]: {
              borderBottomLeftRadius: newBorderRadius,
              borderBottomRightRadius: newBorderRadius,
            },
          },
        }
      : {}),
    border: 'none',
    background: theme.palette.table.background,
    // Override MUI's internally computed border color so all borders are consistent with the table palette.
    // MUI computes border color from theme.palette.divider using lighten/darken, but we want to use the
    // table palette's borderColor directly.
    [`& .${gridClasses.cell}, & .${gridClasses.columnHeaders}`]: {
      borderBottomColor: theme.palette.table.borderColor,
    },
    [`& .${gridClasses.cell}`]: {
      // Let row background (including hover / selected) show through instead of MUI/paper default fills.
      background: 'inherit',
    },
    [`& .${gridClasses.cell}:focus-within, & .${gridClasses.cell}:focus`]: {
      outline: 'none',
    },
    [`& .${gridClasses.row}.disabled`]: {
      opacity: 0.7,
    },
    [`& .${gridClasses.row}.${rowSelectedClassName}`]: {
      background: theme.palette.table.rowSelectedBackground,
      '&:hover, &.Mui-hovered': {
        background: theme.palette.table.rowSelectedBackground,
      },
    },
    [`& .${gridClasses.row}:not(.${rowSelectedClassName}):not(.disabled):hover, & .${gridClasses.row}:not(.${rowSelectedClassName}):not(.disabled).Mui-hovered`]:
      {
        background: theme.palette.table.rowHoverBackground,
      },
    [`& .${gridClasses.row}:not(.${rowSelectedClassName}):not(.disabled):hover .${gridClasses.cellCheckbox}, & .${gridClasses.row}:not(.${rowSelectedClassName}):not(.disabled).Mui-hovered .${gridClasses.cellCheckbox}`]:
      {
        background: theme.palette.table.rowHoverBackground,
      },

    [`& .${gridClasses.columnHeaderTitle}`]: {
      fontWeight: theme.typography.fontWeightBold,
    },
    // TODO: Remove these overrides when upgrading MUI to v7. It looks like they replace the box-shadows
    // with borders in that version.
    [`.${gridClasses.pinnedColumnHeaders}, .${gridClasses.pinnedColumns}`]: {
      backgroundColor: theme.featureFlags.panwRebrandStubYear ? theme.palette.table.background : undefined,
      backgroundImage: 'none',
      boxShadow: 'none',

      [`&.${gridClasses['pinnedColumnHeaders--left']}, &.${gridClasses['pinnedColumns--left']}`]: {
        borderRight: `solid 1px ${theme.palette.table.borderColor}`,
      },
      [`&.${gridClasses['pinnedColumnHeaders--right']}, &.${gridClasses['pinnedColumns--right']}`]: {
        borderLeft: `solid 1px ${theme.palette.table.borderColor}`,
      },
    },

    // From MUI's v7 recipe docs. Hide the draggable column separator for columns that are not resizable.
    // https://mui.com/x/react-data-grid/style-recipes/#remove-drag-handle-for-columns-that-are-not-resizeable
    [`& .${gridClasses.columnSeparator}`]: {
      [`&:not(.${gridClasses['columnSeparator--resizable']})`]: {
        display: 'none',
      },
    },

    // Styles only used when `enableExpandableCells` is `true` and the associated class name is set on
    // the table.
    // NOTE: we will very likely need to re-work this logic when we do the next major MUI upgrade because
    // they change some of the default cell rendering logic and class names.
    [`&.${ENABLE_EXPANDABLE_CELLS_CLASS_NAME}`]: {
      // .Mui-hovered also needed for floating columns, which do not get the `:hover`. Not solely
      // relying on this class because it seems slightly laggier in some cases than `:hover` (likely because
      // it's set via JS whereas `:hover` is a native CSS feature).
      [`& .${gridClasses.row}:hover, & .${gridClasses.row}.Mui-hovered`]: {
        // We set the row background when hovered, so it can be inherited in cell content.
        // A solid value is needed here to avoid overlapping backgrounds with alpha values, which
        // will lead to some inconsistent colors as the opacities overlap.
        background: theme.palette.table.rowHoverBackground,
      },
      [`& .${gridClasses.cell}`]: {
        // By default, inherit the background from the row, so that the cells keep the hover background
        // color. This is the best spot for downstream use cases to override a cell's background color
        // and have it work properly with the "expand" behavior in the `cellContent` below.
        background: 'inherit',

        '.Badge': {
          // Force vertical alignment of inline-flex badges in expandable data table cells to the top
          // to avoid weird math for the hover behavior (it being centered sometimes adds pixels to the
          // parent).
          verticalAlign: 'top',
        },
      },
      // Checkbox cell needs more specificity to override MUI's default styling
      [`& .${gridClasses.row}:hover .${gridClasses.cellCheckbox}, & .${gridClasses.row}.Mui-hovered .${gridClasses.cellCheckbox}`]:
        {
          background: theme.palette.table.rowHoverBackground,
        },
      [`& .${gridClasses.cell}:hover`]: {
        // Needed so the cell content can be absolutely positioned and anchor itself within the cell.
        position: 'relative',
        // Enables the cell content to expand outside of the boundaries of the cell to show all content.
        overflow: 'visible',

        [`& .${gridClasses.cellContent}`]: {
          // Show a border when overflowing content to make it easier to visually identify the cell.
          border: `solid 1px ${theme.palette.table.borderColor}`,

          // Try to retain alignment set via flexbox on parent cell, so that the MUI column alignment
          // features from the column defs continue to work in most use cases.
          display: 'flex',
          justifyContent: 'inherit',
          alignItems: 'inherit',
          background: 'inherit',

          // Account for empty or cells with limited content to ensure they fill the full space.
          // Important for backgrounds and borders to look reasonable in those cases.
          width: '100%',
          // Full height plus borders.
          minHeight: 'calc(100% + 2px)',

          // Force text to wrap, so that long text does not overflow to the right.
          overflow: 'visible',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          whiteSpace: 'normal',

          // Absolutely position so we can expand out of the parent.
          position: 'absolute',
          left: 0,
          right: 0,
          // Overlap top border with row, so we do not see a double border.
          top: `-1px`,

          // Matching 10px padding from `GridRootStyles`.
          // https://github.com/mui/mui-x/blob/v6.x/packages/grid/x-data-grid/src/components/containers/GridRootStyles.ts#L176
          padding: 10,
          // Padding above minus 1px to account for borders.
          paddingLeft: 9,
          paddingRight: 9,

          // Arbitrary large z-index to hopefully show cell above other rows.
          zIndex: 10,

          '.Badge': {
            // Simple handling for known, small Badge use cases in listing pages to avoid the Badges shifting
            // down on hover.
            // Numbers obtained by looking at height of `MuiDataGrid-cellContent` for cells with
            // Badges at rest (22px), subtracting it by the expected height of the body2 text for tables (18px) and
            // dividing by 2.
            // This will need more complex refactoring to handle wider badge use cases.
            marginTop: `calc((22px - ${theme.typography.body2.lineHeight}) / 2 * -1)`,
            marginBottom: `calc((22px - ${theme.typography.body2.lineHeight}) / 2 * -1)`,
          },
          [`.${chipClasses.root}`]: {
            // Simple handling for common Chip use cases in listing pages to avoid the Chips shifting down on
            // hover.
            // Numbers obtained by looking at the height of chips (should always be the same because we do not support
            // sizes for Chips), subtracting it by the expected height of the body2 text for tables (18px) and
            // dividing by 2.
            marginTop: `calc((${CHIP_SMALL_HEIGHT} - ${theme.typography.body2.lineHeight}) / 2 * -1)`,
            marginBottom: `calc((${CHIP_SMALL_HEIGHT} - ${theme.typography.body2.lineHeight}) / 2 * -1)`,
          },
        },
        [`.${EXPANDABLE_CELL_NESTED_TEXT_CONTENT_CLASS_NAME}`]: {
          overflow: 'auto',
          textOverflow: 'normal',
        },
        [`.${EXPANDABLE_CELL_FLEX_WRAP_CLASS_NAME}`]: {
          flexWrap: 'wrap',
        },
      },
      [`.${EXPANDABLE_CELL_NESTED_TEXT_CONTENT_CLASS_NAME}`]: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      [`.${EXPANDABLE_CELL_FLEX_WRAP_CLASS_NAME}`]: {
        flexWrap: 'nowrap',
      },
    },
  };
});
